import { useCallback, useEffect, useState, type RefObject } from 'react';
import type { IChartApi } from 'lightweight-charts';

export function resolveChartPaneTop(
  chart: IChartApi,
  container: HTMLElement,
  paneIndex: number,
): number | null {
  const pane = chart.panes()[paneIndex];
  if (!pane) return null;

  const paneEl = pane.getHTMLElement();
  if (paneEl) {
    const containerRect = container.getBoundingClientRect();
    const paneRect = paneEl.getBoundingClientRect();
    return paneRect.top - containerRect.top;
  }

  const panes = chart.panes();
  if (panes.length <= paneIndex) return null;
  let top = 0;
  for (let index = 0; index < paneIndex; index += 1) {
    top += panes[index]?.getHeight() ?? 0;
    top += 1;
  }
  return top;
}

type UseChartPaneOverlayPositionOptions = {
  chartRef: RefObject<IChartApi | null>;
  chartContainerRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
  paneCollapsed: boolean;
  paneIndex: number;
  chartRevision?: number;
};

export function useChartPaneOverlayPosition({
  chartRef,
  chartContainerRef,
  enabled,
  paneCollapsed,
  paneIndex,
  chartRevision = 0,
}: UseChartPaneOverlayPositionOptions): number | null {
  const [top, setTop] = useState<number | null>(null);

  const updatePosition = useCallback(() => {
    const chart = chartRef.current;
    const container = chartContainerRef.current;
    if (!chart || !container || !enabled || paneCollapsed) {
      setTop(null);
      return;
    }

    setTop(resolveChartPaneTop(chart, container, paneIndex));
  }, [chartContainerRef, chartRef, enabled, paneCollapsed, paneIndex]);

  useEffect(() => {
    updatePosition();
  }, [chartRevision, updatePosition]);

  useEffect(() => {
    if (!enabled) {
      setTop(null);
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;
    let boundChart: IChartApi | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let boundContainer: HTMLDivElement | null = null;

    const cleanupListeners = () => {
      if (boundChart) {
        boundChart.timeScale().unsubscribeSizeChange(updatePosition);
        boundChart = null;
      }
      if (boundContainer) {
        boundContainer.removeEventListener('mouseup', updatePosition);
        boundContainer = null;
      }
      resizeObserver?.disconnect();
      resizeObserver = null;
    };

    const bindWhenReady = () => {
      if (cancelled) return;

      const chart = chartRef.current;
      const container = chartContainerRef.current;
      if (!chart || !container) {
        rafId = window.requestAnimationFrame(bindWhenReady);
        return;
      }

      if (boundChart !== chart || boundContainer !== container) {
        cleanupListeners();
        boundChart = chart;
        boundContainer = container;
        chart.timeScale().subscribeSizeChange(updatePosition);
        resizeObserver = new ResizeObserver(updatePosition);
        resizeObserver.observe(container);
        container.addEventListener('mouseup', updatePosition);
      }

      updatePosition();
    };

    bindWhenReady();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      cleanupListeners();
    };
  }, [chartContainerRef, chartRef, chartRevision, enabled, updatePosition]);

  return top;
}
